import { Theme } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import React, { ReactElement, useState, useEffect, useCallback } from "react";
import { Flipper } from "react-flip-toolkit";
import { ImageCard } from "./index";
import { ViImageData, Filter } from "../models/";
import clsx from "clsx";
import { useVi } from "../contexts/ViContext";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    gridArea: "gallery",
    boxSizing: "border-box",
    position: "relative",
    overflowY: "hidden",
    overflowX: "visible",
    [theme.breakpoints.down("sm")]: {
      height: "min-content",
      overflowY: "visible",
    },
  },
  list: {
    [theme.breakpoints.down("sm")]: {
      height: "auto",
      overflowY: "visible",
    },
    position: "relative",
    maxHeight: "100%",
    padding: "0.618rem",
    boxSizing: "border-box",
    overflowY: "auto",
    listStyle: "none",
    display: "flex",
    flexFlow: "row wrap",
    alignContent: "flex-start",
    margin: 0,
    height: "100%",
    "& > li": {
      margin: "0 0.382rem 0.382rem 0",
    },
  },
}));

function ImageGallery({
  imageData,
  onImageDelete,
  setHoverFilter,
  hoverFilter,
  isLoading,
  canFlip,
}: {
  imageData: ViImageData[];
  onImageDelete: (image: ViImageData) => void;
  setHoverFilter: (filters: Filter[]) => void;
  hoverFilter: Filter[];
  isLoading: boolean;
  canFlip: boolean;
}): ReactElement {
  const [data, setData] = useState<ViImageData[] | undefined>(imageData);

  const [focusIndex, setFocusIndex] = useState("");
  const [fullViewIndex, setFullViewIndex] = useState("");
  const { showUndamaged, showRejected, showDuplicates } = useVi();
  const classes = useStyles();

  function getDamagedParts(
    imageData: ViImageData[],
    { unique = false }: { unique?: boolean } = {}
  ) {
    return imageData.map((image) => {
      let damagedParts = image.damages?.map(
        (damage) => damage.imagePart.identifiers.genericPartName
      );
      if (unique) damagedParts = Array.from(new Set(damagedParts));
      return {
        imageRef: image,
        damagedParts,
      };
    });
  }

  function sortByDamageCount(imageList: ViImageData[]) {
    return imageList.sort(
      (a, b) => (a.damages && b.damages && b.damages.length - a.damages.length) || 0
    );
  }

  const filterOutUndamagedImages = useCallback((imageDataArray: ViImageData[]) => {
    const imagesWithDamagedParts = getDamagedParts(imageDataArray);
    return imagesWithDamagedParts
      .filter(({ damagedParts, imageRef }) => {
        return (
          (damagedParts && damagedParts.length > 0) ||
          !imageRef?.status ||
          imageRef?.status?.toUpperCase() === "REJECTED"
        );
      })
      .map((image) => image.imageRef);
  }, []);

  const filterOutRejectedImages = useCallback((imageDataArray: ViImageData[]) => {
    return imageDataArray.filter(
      ({ status = "", isDuplicate }) => status.toUpperCase() !== "REJECTED" || isDuplicate
    );
  }, []);

  const filterOutDuplicateImages = useCallback((imageDataArray: ViImageData[]) => {
    return imageDataArray.filter(({ isDuplicate }) => !isDuplicate);
  }, []);

  const sortByMatchCount = useCallback(
    (imageDataArray: ViImageData[], focusImage: ViImageData) => {
      const imagesWithDamagedParts = getDamagedParts(imageDataArray);
      const [focusImageWithDamagedParts] = getDamagedParts([focusImage], {
        unique: true,
      });
      const imagesWithMatchCount = imagesWithDamagedParts.map((image) => {
        const count = image.damagedParts?.reduce<number>((count, damagedPart): number => {
          if (focusImageWithDamagedParts?.damagedParts?.includes(damagedPart)) count++;
          return count;
        }, 0);
        return { imageRef: image.imageRef, count };
      });

      return imagesWithMatchCount
        .sort((a, b) => (a.count && b.count && b.count - a.count) || 0)
        .map((element) => {
          return element.imageRef;
        });
    },
    []
  );

  const getSortedImageList = useCallback(
    (
      imageDataArray: ViImageData[],
      showDuplicates: boolean,
      showUndamaged: boolean,
      showRejected: boolean,
      focusImage?: ViImageData
    ) => {
      let sortedImageDataArray = imageDataArray;

      if (!showUndamaged)
        sortedImageDataArray = filterOutUndamagedImages(sortedImageDataArray);
      if (!showRejected)
        sortedImageDataArray = filterOutRejectedImages(sortedImageDataArray);
      if (!showDuplicates)
        sortedImageDataArray = filterOutDuplicateImages(sortedImageDataArray);

      // if no sorting criteria (i.e. there's no focusImage) are provided sort based on damage count
      if (focusImage === undefined || focusImage.damages?.length === 0) {
        sortedImageDataArray = sortByDamageCount(sortedImageDataArray);
      } else {
        sortedImageDataArray = sortByMatchCount(imageDataArray, focusImage);
      }
      return isLoading
        ? sortedImageDataArray
        : sortedImageDataArray.sort(
            (a, b) =>
              (b.error ? 0 : 1) - (a.error ? 0 : 1) ||
              (b.isDuplicate ? 0 : 1) - (a.isDuplicate ? 0 : 1)
          );
    },
    [
      filterOutRejectedImages,
      filterOutUndamagedImages,
      filterOutDuplicateImages,
      sortByMatchCount,
      isLoading,
    ]
  );

  const handleOffClick = useCallback(() => {
    setFullViewIndex("");
    setFocusIndex("");
  }, []);

  const handleClick = useCallback(
    (imageData: ViImageData, focus: boolean, fullView: boolean) => {
      if (data && !(focus || fullView) && canFlip) {
        setFocusIndex(imageData.id);
        setData(
          getSortedImageList(data, showDuplicates, showUndamaged, showRejected, imageData)
        );
      } else if (focus) {
        setFullViewIndex(imageData.id);
        setFocusIndex("");
      }
    },
    [data, getSortedImageList, showRejected, showUndamaged, showDuplicates, canFlip]
  );

  useEffect(() => {
    if (imageData)
      setData(getSortedImageList(imageData, showDuplicates, showUndamaged, showRejected));
  }, [imageData, showDuplicates, showUndamaged, showRejected, getSortedImageList]);

  return (
    <>
      {data && data?.length > 0 ? (
        <Flipper
          flipKey={
            window.Cypress //prevent flipping when code is run in cypress
              ? ""
              : `${focusIndex}-${fullViewIndex}-${data
                  .map((img) => img.filename)
                  .join("-")}`
          }
          className={clsx("gallery", classes.container)}
          spring="veryGentle"
          staggerConfig={{
            default: {
              speed: 1,
            },
          }}
          decisionData={{ focusIndex, fullViewIndex }}
        >
          <ul className={classes.list} onClick={handleOffClick}>
            {data.map((imageData: ViImageData, idx: number) => {
              return (
                <ImageCard
                  focus={focusIndex === imageData.id}
                  fullView={fullViewIndex === imageData.id}
                  key={imageData.localId}
                  goBack={handleOffClick}
                  onClick={handleClick}
                  final={data.length === 1}
                  {...{
                    onImageDelete,
                    hoverFilter,
                    setHoverFilter,
                    isLoading,
                    imageData,
                    canFlip,
                  }}
                />
              );
            })}
          </ul>
        </Flipper>
      ) : null}
    </>
  );
}

export default React.memo(ImageGallery);
